import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from "../../../api/Base";

// Types.
import Action from '../../../types/Action';
import LoginTags from "../../../types/LoginsTags";

// Constants.
import { ACTIONS } from '../../../component/Constants';
import Alert from "../../../component/Alert";
import { TextInput, PasswordField, CheckboxField } from '../../../components/forms';
import LoginApi from "../../../api/LoginApi";
import ConditionalRender from "../../../helpers/ConditionalRender";
import LoginsDataTable from "../../../types/LoginsDataTable";
import TagsApi from "../../../api/TagsApi";

// Extended login type for SSO information
interface ExtendedLogin extends LoginsDataTable {
    sso_provider?: string;
    sso_email?: string;
}

function Detail(props: { tags: LoginTags[], refresh: Function, logins: LoginsDataTable[] }) {
    const { actionId, loginId } = useParams();
    const [action, setAction] = useState<Action>(ACTIONS.CREATE);
    const [isError, setIsError] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(actionId === ACTIONS.DELETE.type);
    const [name, setName] = useState('');
    const [invalidName, setInvalidName] = useState<string>('');
    const [nameUsed, setNameUsed] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [invalidPassword, setInvalidPassword] = useState<string>('');
    const [passwordMismatch, setPasswordMismatch] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [loading, setLoading] = useState<boolean>(true);

    const [selectedTags, setSelectedTags] = useState<LoginTags[]>([]);
    const [tagList, setTagList] = useState<LoginTags[]>([]);
    const [tagsToDelete, setTagsToDelete] = useState<LoginTags[]>([]);

    const [editDetails, setEditDetails] = useState<ExtendedLogin>();
    const nameValueRef = useRef('');

    const navigate = useNavigate();
    useEffect(() => {
        getAction();
        void getLoginDetails();
        void getAllTags();
    }, [actionId]);

    const getAllTags = async() => {
        let allTagsList: any[] = await TagsApi.getAllTags();
        const sortedTags = allTagsList.sort((a, b) => {
            return a.tagname.localeCompare(b.tagname);
        });
        setTagList(sortedTags)
    }

    const getLoginDetails = async() => {

        if (loginId) {
            let currentLogin: any[] = await LoginApi.getLogins(Number(loginId))
            setEditDetails(currentLogin[0])
            const loginName = currentLogin[0].name;
            setName(loginName)
            nameValueRef.current = loginName;
            setIsAdmin(currentLogin[0].admin)
        }
        setLoading(false)
    }

    useEffect(() => {
        let tagsSelected: any[] = []
        if (loginId) {
            tagsSelected = props.tags.filter(tag => tag.loginid === Number(loginId))
        }
        setLoading(false)
        setSelectedTags(tagsSelected)

    }, [props.tags, loginId]);

    const shouldValidatePassword = action.type === 'create' || (action.type === 'update' && !!password);
    const shouldSkipPasswordValidation = !!editDetails?.sso_provider;

    useEffect(() => {
        if (invalidPassword !== 'is-invalid') {
            return;
        }

        if (shouldSkipPasswordValidation || !shouldValidatePassword) {
            setInvalidPassword('');
            return;
        }

        if (password.length >= 8) {
            setInvalidPassword('');
        }
    }, [invalidPassword, password, shouldSkipPasswordValidation, shouldValidatePassword]);

    useEffect(() => {
        if (!passwordMismatch) {
            return;
        }

        if (password === confirmPassword) {
            setPasswordMismatch(false);
        }
    }, [passwordMismatch, password, confirmPassword]);


    const getAction = () => {

        switch (actionId) {
            case 'create':
                setAction(ACTIONS.CREATE);
                break;

            case 'edit':
                setAction(ACTIONS.UPDATE);
                break;

            case 'delete':
                setAction(ACTIONS.DELETE);
                setDisabled(true);
                break;

            default:
                console.error('Unknown action requested', actionId);
                setIsError(true);
                break;
        }
    }

    const handleTags = (tagname: string, tagvalue: string) => {
        const currentTag = tagList.find(tag => tag.tagvalue === tagvalue && tag.tagname === tagname);
        if (!currentTag) {
            console.error('Tag not found in tagList');
            return;
        }

        const tagExist = selectedTags.find(tag => tag.tagvalue === tagvalue && tag.tagname === tagname);

        if (tagExist) {
            if (tagExist.id && !tagsToDelete.some(tag => tag.id === tagExist.id)) {
                setTagsToDelete([...tagsToDelete, tagExist]);
            }

            setSelectedTags(selectedTags.filter(tag => !(tag.tagvalue === tagvalue && tag.tagname === tagname)));
        } else {
            const newTag = { ...currentTag, loginid: Number(loginId) };
            setSelectedTags([...selectedTags, newTag]);
        }
    };
    
    const submitForm = async() => {
        setLoading(true);
        setErrorMessage('');
        setInvalidName('');
        setInvalidPassword('');
        setPasswordMismatch(false);

        if (!name) {
            setInvalidName('is-invalid');
            setLoading(false);
            return false;
        }

        if (props.logins.find(item => item.name === name) && action.type === 'create') {
            setNameUsed(true);
            setLoading(false);
            return false;
        }

        // Password validation for create and update operations (skip for SSO users)
        if (shouldValidatePassword && !shouldSkipPasswordValidation) {
            if (!password) {
                setInvalidPassword('is-invalid');
                setLoading(false);
                return false;
            }
            if (password.length < 8) {
                setInvalidPassword('is-invalid');
                setLoading(false);
                return false;
            }
            if (password !== confirmPassword) {
                setPasswordMismatch(true);
                setLoading(false);
                return false;
            }
        }

        const loginData = [{
            id: Number(loginId),
            name,
            admin: isAdmin,
            // Only include password for non-SSO users
            ...(password && !editDetails?.sso_provider && { password }),
        }];
        const newTags = selectedTags.filter(item => !item.hasOwnProperty('id'));

        let url = `/login`;
        let tagUrl = `/login/tag`;

        try {
            let result;
            let tagResult;
            let deleteTagResult;

            switch (action.type) {
                case 'create':
                    result = await api.post(url, loginData);
                    break;

                case 'update':
                    result = await api.put(url, loginData);
                    break;

                case 'delete':
                    result = await api.delete(url, { data: loginData });
                    break;

                default:
                    console.error('Unknown action requested', action);
                    return;
            }


            if (result.status !== 200) {
                setErrorMessage('Error creating or updating user');
                return;
            } else {
                // if create user was successful add tags

                // add loginid for tags is the user is newly created
                if (action.type === 'create' && result.data.length) {
                    const newLoginId = result.data[0]
                    // Update the loginid field for each object in newTags
                    newTags.forEach(tag => {
                        tag.loginid = newLoginId;
                    });
                }

                try {
                    if (tagsToDelete.length && action.type === 'update') {
                        deleteTagResult = await api.delete(tagUrl, { data: tagsToDelete });
                    }

                    if (tagsToDelete.length && action.type === 'delete') {
                        // delete all tags assigned to that user
                        let tagsSelected: any[] = []
                        if (loginId) {
                            tagsSelected = props.tags.filter(tag => tag.loginid === Number(loginId))
                        }
                        deleteTagResult = await api.delete(tagUrl, { data: [tagsSelected] });
                        if (deleteTagResult?.status !== 200) {
                            setErrorMessage('Error deleting tags');
                            return;
                        }
                    }

                    if (newTags.length) {
                        tagResult = await api.post(tagUrl, newTags);
                        if (tagResult?.status !== 200) {
                            setErrorMessage('Error creating tags');
                            return;
                        }
                    }
                } catch (tagError) {
                    console.error('Error handling tags:', tagError);
                    setErrorMessage('Error handling tags');
                    return;
                }
            }

            navigate(-1)
            props.refresh();

        } catch (error) {
            console.error('Error during API call:', error);
            setErrorMessage('An error occurred while submitting the form');
        } finally {
            setLoading(false);
        }
    }
    const handleNameBlur = () => {
        const value = nameValueRef.current;
        const isNameTaken = props.logins.find(item => item.name === value);
        const isCreating = action.type === 'create';
        const isEditingDifferentName = action.type === 'update' && editDetails?.name !== value;

        if (!value) {
            setInvalidName('is-invalid');
        } else if (isNameTaken && (isCreating || isEditingDifferentName)) {
            setNameUsed(true);
            setInvalidName('is-invalid');
        } else {
            setInvalidName('is-valid');
            setNameUsed(false);
        }
    }

    return (
        <div className="col">
            <div className="card">

                <div className="card-header">
                    <i className="fal fa-server fa-fw" aria-hidden="true"></i>
                    {action.title} user login
                </div>


                <div className="card-body">

                    {isError && (
                        <Alert
                            message="Sorry, but something went wrong and the requested action failed.  If the issue persists, please raise a support request."
                            heading={`Failed to ${action.title.toLowerCase()} login user`} variant="alert-danger"/>
                    )}
                    {errorMessage && (
                        <Alert
                            message={errorMessage}
                            heading='Error' variant="alert-danger"/>
                    )}

                    <div className="row g-0 form">
                        {/* Name */}
                        <TextInput
                            id="name"
                            label="Login name"
                            value={name}
                            onChange={(value) => {
                                const sanitized = value.replace(/\s+/g, '');
                                nameValueRef.current = sanitized;
                                setName(sanitized);
                            }}
                            onBlur={handleNameBlur}
                            required
                            maxLength={250}
                            disabled={disabled}
                            dataLpIgnore
                            error={invalidName === 'is-invalid' ? (nameUsed ? 'Login name already used.' : '') : undefined}
                            className={invalidName}
                        />

                        {/* Password */}
                        <ConditionalRender if={action.type === 'create' || (action.type === 'update' && !editDetails?.sso_provider)}>
                            <PasswordField
                                id="password"
                                label="Password"
                                value={password}
                                onChange={e => {
                                    setPassword(e.target.value);
                                }}
                                required={action.type === 'create'}
                                disabled={disabled}
                                dataLpIgnore
                                canToggleVisibility
                                autoComplete="new-password"
                                placeholder={action.type === 'update' ? 'Leave blank to keep current password' : ''}
                                error={invalidPassword === 'is-invalid' ? (password.length < 8 ? 'Password must be at least 8 characters long.' : 'Password is required.') : undefined}
                                className={invalidPassword}
                            />
                            <PasswordField
                                id="confirmPassword"
                                label="Confirm Password"
                                value={confirmPassword}
                                onChange={e => {
                                    setConfirmPassword(e.target.value);
                                }}
                                required={action.type === 'create'}
                                disabled={disabled}
                                dataLpIgnore
                                canToggleVisibility
                                autoComplete="new-password"
                                placeholder={action.type === 'update' ? 'Leave blank to keep current password' : ''}
                                error={passwordMismatch ? 'Passwords do not match.' : undefined}
                                className={passwordMismatch ? 'is-invalid' : ''}
                            />
                        </ConditionalRender>

                        <CheckboxField
                            id="admin"
                            label="Administrator"
                            checked={isAdmin}
                            onChange={setIsAdmin}
                            disabled={disabled}
                        />

                        {/* SSO Information */}
                        <ConditionalRender if={editDetails?.sso_provider}>
                            <div className="col-xs-12 col-md-3">
                                <label className="reminder">SSO Provider</label>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <div className="form-control-plaintext">
                                    <i className="fas fa-shield-alt text-primary me-2" aria-hidden="true"></i>
                                    {editDetails?.sso_provider}
                                </div>
                            </div>
                            <div className="w-100"/>
                        </ConditionalRender>

                        <ConditionalRender if={editDetails?.sso_email}>
                            <div className="col-xs-12 col-md-3">
                                <label className="reminder">SSO Email</label>
                            </div>
                            <div className="col-xs-12 col-md-6 col-lg-4">
                                <div className="form-control-plaintext">
                                    <i className="fas fa-envelope text-muted me-2" aria-hidden="true"></i>
                                    {editDetails?.sso_email}
                                </div>
                            </div>
                            <div className="w-100"/>
                        </ConditionalRender>

                        <ConditionalRender if={editDetails?.sso_provider}>
                            <div className="col-xs-12">
                                <Alert
                                    message="This user authenticates via SSO. Password changes must be made through the identity provider."
                                    heading="SSO User"
                                    variant="alert-info"
                                />
                            </div>
                            <div className="w-100"/>
                        </ConditionalRender>

                        <ConditionalRender if={isAdmin}>
                            <Alert message='Administrators have access to all datasources' heading="Tags"
                                   variant="alert-warning"/>
                        </ConditionalRender>
                        <div className="col-xs-12 col-md-3">
                            <label className="reminder">Tags</label>
                        </div>
                        <div className="col-xs-12 col-md-6 col-lg-3">
                            <div className="list-group">
                                {tagList.map((item, index) => (
                                    <label key={index} className="list-group-item">
                                        <input className="form-check-input me-2" type="checkbox" value={item.id}
                                               checked={selectedTags.filter(tag => item.tagname === tag.tagname && item.tagvalue === tag.tagvalue).length > 0}
                                               disabled={disabled || isAdmin}
                                               onChange={() => handleTags(item.tagname, item.tagvalue)}/>
                                        {`${item.tagname} : ${item.tagvalue}`}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="w-100"/>

                        <div className="col-12 actions">
                            <button onClick={() => navigate(-1)} className="btn btn-secondary"><i
                                    className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span></button>
                            <button onClick={() => submitForm()}
                                    disabled={loading || !name || nameUsed || invalidPassword === 'is-invalid' || passwordMismatch}
                                    className={`btn btn-${action.variant}`}>
                                <i className={`${action.icon} fa-sm`}/><span>{action.title}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Detail;
